import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type SubscriptionPlan = "free" | "pro" | "business";
export type SubscriptionStatus = "active" | "canceled" | "expired" | "trialing";

// Secure subscription interface - no sensitive payment data exposed
export interface Subscription {
  id: string;
  user_id: string;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  current_period_start: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  features: {
    max_generations_per_day?: number;
    max_resolution?: string;
    priority_queue?: boolean;
  } | null;
  monthly_credits: number;
  created_at: string;
  updated_at: string;
}

const planFeatures = {
  free: {
    name: "Gratuit",
    monthlyCredits: 10,
    maxGenerationsPerDay: 10,
    maxResolution: "1024x1024",
    priorityQueue: false,
    features: ["10 crédits/mois", "Résolution HD", "Export PNG/JPG"],
  },
  pro: {
    name: "Pro",
    monthlyCredits: 250,
    maxGenerationsPerDay: -1,
    maxResolution: "1920x1920",
    priorityQueue: true,
    features: ["250 crédits/mois", "Résolution 4K", "Accès API", "Support prioritaire"],
  },
  business: {
    name: "Studio Max",
    monthlyCredits: 500,
    maxGenerationsPerDay: -1,
    maxResolution: "1920x1920",
    priorityQueue: true,
    features: ["500 crédits/mois", "Multi-utilisateurs", "API prioritaire", "Account manager"],
  },
};

export function useSubscription() {
  const { data: subscription, isLoading, error } = useQuery({
    queryKey: ["user-subscription"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // Use secure RPC function - never exposes Stripe columns
      const { data, error } = await supabase
        .rpc("get_user_subscription", { p_user_id: user.id });

      if (error) throw error;
      
      // RPC returns array, get first item
      const subscriptionData = Array.isArray(data) ? data[0] : data;
      return subscriptionData as Subscription | null;
    },
  });

  const plan = subscription?.plan || "free";
  const status = subscription?.status || "active";
  const isActive = status === "active" || status === "trialing";
  const isPro = plan === "pro" || plan === "business";
  const isBusiness = plan === "business";

  const currentPlanFeatures = planFeatures[plan];

  const daysUntilExpiration = subscription?.current_period_end
    ? Math.ceil((new Date(subscription.current_period_end).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  return {
    subscription,
    isLoading,
    error,
    plan,
    status,
    isActive,
    isPro,
    isBusiness,
    currentPlanFeatures,
    daysUntilExpiration,
    cancelAtPeriodEnd: subscription?.cancel_at_period_end || false,
  };
}
